/**
 * Fixed bottom navigation bar. Unauthenticated: shows a sign-up CTA instead of user links.
 * Authenticated: Home / Search / Notifications (badge polled every 30 s) / Profile.
 */
import { Home, Search, Bell, User, MessageCircle, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth.store'
import { notificationsApi, messagesApi } from '@/api'

const REFRESH_INTERVAL = 30_000

export function BottomNav() {
  const { t } = useTranslation()
  const { isAuthenticated, account, isRestoring } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: REFRESH_INTERVAL,
    refetchIntervalInBackground: false,
  })

  const { data: unreadDmCount = 0 } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: () => messagesApi.getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: REFRESH_INTERVAL,
    refetchIntervalInBackground: false,
  })

  // While the auth store is rehydrating (every full page reload, since the
  // token is never persisted — see auth.store.ts), isAuthenticated is
  // momentarily false. Rendering the shell at the same height avoids both
  // a layout jump and a flash of the signed-out "Sign up" bar for someone
  // who's actually logged in.
  if (isRestoring) {
    return (
      <nav
        className="lg:hidden"
        style={{
          flexShrink: 0,
          height: 'calc(var(--bottom-nav) + env(safe-area-inset-bottom, 0px))',
          background: 'var(--surface)',
          borderTop: '1px solid var(--border)',
        }}
      />
    )
  }

  if (!isAuthenticated) {
    return (
      <nav
        className="lg:hidden"
        style={{
          flexShrink: 0,
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <button
          onClick={() => navigate('/signup')}
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            border: 'none',
            cursor: 'pointer',
            background: 'var(--surface)',
            padding: 'calc(13px + env(safe-area-inset-bottom, 0px)) 16px 13px',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>{t('welcome.bottom')}</span>
          <span
            style={{
              color: 'var(--brand)',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {t('welcome.bottom_cta')} <ChevronRight size={15} />
          </span>
        </button>
      </nav>
    )
  }

  const NAV_ITEMS = [
    { to: '/', label: t('nav.home'), Icon: Home, matchExact: true, badge: 0 },
    { to: '/search', label: t('nav.search'), Icon: Search, matchExact: false, badge: 0 },
    { to: '/notifications', label: t('nav.notifications'), Icon: Bell, matchExact: false, badge: unreadCount },
    { to: '/messages', label: t('nav.messages'), Icon: MessageCircle, matchExact: false, badge: unreadDmCount },
    { to: account ? `/@${account.username}` : '/settings', label: t('nav.profile'), Icon: User, matchExact: false, badge: 0 },
  ]

  return (
    <nav
      className="flex lg:hidden"
      style={{
        flexShrink: 0,
        alignItems: 'stretch',
        height: 'calc(var(--bottom-nav) + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        zIndex: 'var(--z-tabbar)',
      }}
    >
      {NAV_ITEMS.map(({ to, label, Icon, matchExact, badge }) => {
        const active = matchExact
          ? location.pathname === to
          : location.pathname.startsWith(to) && to !== '/'
        return (
          <Link
            key={to}
            to={to}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              textDecoration: 'none',
              color: active ? 'var(--brand)' : 'var(--text-muted)',
              minHeight: 'var(--tap-min)',
              position: 'relative',
            }}
          >
            <span style={{ position: 'relative', display: 'flex' }}>
              <Icon size={24} />
              {badge > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -4,
                    insetInlineEnd: -6,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 'var(--radius-pill)',
                    background: 'var(--danger)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    fontFamily: 'var(--font-sans)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 3px',
                    lineHeight: 1,
                  }}
                >
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </span>
            <span style={{ fontSize: 10, fontWeight: 600, fontFamily: 'var(--font-sans)' }}>
              {label}
            </span>
          </Link>
        )
      })}
    </nav>
  )
}
